import { Component, DOCUMENT, inject, input, OnInit } from '@angular/core';
import { Router, RouterModule, RouterOutlet } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { LucideAngularModule } from 'lucide-angular';
import { lastValueFrom } from 'rxjs';
import { PartnerService } from '../../../core/api/partner.service';
import { Language, LanguageEnum } from '../../../core/models/language.models';
import { ConsentService } from '../analytics/consent.service';
import { CookieBannerComponent } from '../analytics/cookie-banner.component';
import { BottomNavigationComponent } from '../bottom-navigation/bottom-navigation.component';
import { FooterComponent, FooterNavItem } from '../footer/footer.component';
import { HeaderMobileComponent } from '../header/header-mobile.component';
import { HeaderComponent, NavigationItem } from '../header/header.component';
import { ScrollToTopComponent } from '../scroll-to-top/scroll-to-top.component';
import { ToastContainerComponent } from '../toast/toast-container.component';
import { ToastService } from '../toast/toast.service';

@Component({
    selector: 'pax-app-shell',
    templateUrl: './app-shell.component.html',
    styleUrls: ['./app-shell.component.scss'],
    standalone: true,
    imports: [
        RouterOutlet,
        HeaderComponent,
        HeaderMobileComponent,
        BottomNavigationComponent,
        FooterComponent,
        LucideAngularModule,
        ToastContainerComponent,
        RouterModule,
        TranslatePipe,
        ScrollToTopComponent,
        CookieBannerComponent,
    ],
})
export class AppShellComponent implements OnInit {
    private readonly document = inject(DOCUMENT);
    private readonly partnerService = inject(PartnerService);
    private readonly router = inject(Router);
    private readonly translateService = inject(TranslateService);
    private readonly toastService = inject(ToastService);
    private readonly consentService = inject(ConsentService);

    /**
     * The current language code derived from the URL parameter.
     * This is automatically resolved by Angular's router from the URL structure (e.g., '/de/home', '/it/home').
     */
    readonly lang = input.required<Language>();

    readonly availableLanguages: string[] = [LanguageEnum.DE, LanguageEnum.FR, LanguageEnum.IT];

    navigationItems: NavigationItem[] = [];
    navigationItemsMobile: NavigationItem[] = [];

    footerItems: FooterNavItem[] = [];

    constructor() {
        this.consentService.loadStoredConsent();
    }

    async ngOnInit(): Promise<void> {
        // set the language of the document in index.html
        this.document.documentElement.lang = this.lang();

        await lastValueFrom(this.translateService.use(this.lang()));
        this.setNavigationItems(this.lang());
        this.setFooterItems(this.lang());
    }

    setNavigationItems(lang: Language) {
        this.navigationItems = [
            {
                path: `/${lang}/home`,
                label: this.translateService.instant('navigation.home'),
                exact: true,
            },
            {
                path: `/${lang}/policies`,
                label: this.translateService.instant('navigation.policies'),
            },
            {
                path: `/${lang}/inbox`,
                label: this.translateService.instant('navigation.inbox'),
            },
            {
                path: `/${lang}/profile`,
                label: this.translateService.instant('navigation.profile'),
            },
            {
                path: `/${lang}/contact`,
                label: this.translateService.instant('navigation.contact'),
            },
        ];

        this.navigationItemsMobile = [
            {
                path: `/${lang}/home`,
                label: this.translateService.instant('navigation.home'),
                icon: 'house',
            },
            {
                path: `/${lang}/policies`,
                label: this.translateService.instant('navigation.policies'),
                icon: 'shield-check',
            },
            {
                path: `/${lang}/inbox`,
                label: this.translateService.instant('navigation.inbox'),
                icon: 'mail',
            },
            {
                path: `/${lang}/profile`,
                label: this.translateService.instant('navigation.profile'),
                icon: 'user',
            },
            {
                path: `/${lang}/contact`,
                label: this.translateService.instant('navigation.contact'),
                icon: 'message-square',
            },
            {
                path: `/${lang}/form-downloads`,
                label: this.translateService.instant('navigation.formDownload'),
                icon: 'file',
            },
            {
                path: `/${lang}/terms`,
                label: this.translateService.instant('navigation.terms'),
                icon: 'file-text',
            },
        ];
    }

    setFooterItems(lang: Language) {
        this.footerItems = [
            {
                path: `/${lang}/terms`,
                label: this.translateService.instant('navigation.terms'),
            },
        ];
    }

    get isPublicRoute(): boolean {
        return this.router.url.includes('auth') || this.router.url.includes('identificationcode') || this.router.url.includes('terms');
    }

    /**
     * Updates the application language and URL using Router navigation.
     * @param lang The new language code to switch to
     * @param preserveScroll Whether to preserve scroll position after language change
     *
     * @description
     * This method handles language switching in the application. The `preserveScroll` parameter
     * is necessary because the router is configured with `scrollPositionRestoration: 'top'`,
     * which automatically scrolls to the top of the page on navigation.
     *
     * When `preserveScroll` is true (default):
     * - Stores the current scroll position before navigation
     * - Performs language change and URL update
     * - Restores the original scroll position after navigation
     *
     * When `preserveScroll` is false:
     * - Allows the default router behavior to scroll to top
     * - Used primarily for mobile language switching where starting from the top is desired
     */
    async onLanguageChange(lang: Language | string, preserveScroll = true) {
        const scrollPosition = preserveScroll ? [this.document.defaultView?.scrollX, this.document.documentElement.scrollHeight] : null;

        await lastValueFrom(this.translateService.use(lang));

        if (!this.isPublicRoute) {
            await lastValueFrom(this.partnerService.updatePartnerPortalLanguage(lang as Language));
        }

        await this.router.navigateByUrl(this.router.url.replace(/\/[^/]+\//, `/${lang}/`));

        this.setNavigationItems(lang as Language);
        this.setFooterItems(lang as Language);

        this.toastService.success(
            this.translateService.instant('appShell.changePortalLangSuccessDescription'),
            this.translateService.instant('appShell.changePortalLangSuccess'),
        );

        if (preserveScroll) {
            setTimeout(() => {
                this.document.defaultView?.scrollTo(scrollPosition![0] ?? 0, scrollPosition![1] ?? 0);
            }, 0);
        }
    }

    /**
     * Mobile-specific language change handler that always scrolls to top.
     * @param lang The new language code to switch to
     */
    onLanguageChangeMobile(lang: Language | string) {
        return this.onLanguageChange(lang as Language, false);
    }
}
